import { useEffect, useRef, useState, type ReactNode } from 'react';
import { actions } from '@/lib/actions';
import { api, ApiError } from '@/lib/api';
import { formatMoneyShort, formatNumber, formatWhen, formatAgo } from '@/lib/format';
import { useRates } from '@/lib/hooks';
import { closeSheet, openRecord } from '@/lib/overlays';
import { toast, toastError } from '@/lib/toast';
import { Avatar, CompanyMono } from '@/ui/Avatar';
import { Chip, Dot, Spinner, Tabs } from '@/ui/bits';
import { SearchInput } from '@/ui/Field';
import { Icon } from '@/ui/Icon';
import { MenuHead, MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';
import { sameTerm } from '@shared/profiles';
import { firstName, type CompanySheet, type PersonSheet, type RecordSheet, type RecordTab } from '@shared/record';
import type { CompanyLogoResult, RestoreRefusal } from '@shared/types';
import type { ComposerMode } from './Composer';
import { emailRecord } from './Composer';
import { fieldError, useCommit, useRecordPatch } from './edit';
import { openLogCall } from './LogCallDialog';
import { useTags } from './queries';
import { bareUrl, hrefOf } from './util';

// The top of the record sheet: who or what it is, tags and source, the quick actions, key facts and the tabs.

export interface HeaderProps {
  sheet: RecordSheet;
  readOnly: boolean;
  tab: RecordTab;
  onTab: (tab: RecordTab) => void;
  /** Opens the composer in a mode, on the tab that suits it. */
  onCompose: (mode: ComposerMode) => void;
}

/* ---------- the name ---------- */

function Name({ sheet, readOnly }: { sheet: RecordSheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sheet.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const done = useRef(false);

  const stop = () => {
    done.current = true;
    setEditing(false);
    setError(null);
    requestAnimationFrame(() => btn.current?.focus());
  };
  const save = async () => {
    if (done.current || busy) return;
    const next = draft.trim().replace(/\s+/g, ' ');
    if (next === sheet.name) return stop();
    if (!next) {
      setError(sheet.type === 'person' ? 'Enter a name' : 'Enter the company name');
      return;
    }
    setBusy(true);
    try {
      await commit('Name', { body: { name: next }, show: { name: next } }, { body: { name: sheet.name }, show: { name: sheet.name } });
      stop();
    } catch (err) {
      setError(fieldError(err, 'name'));
    } finally {
      setBusy(false);
    }
  };

  if (editing)
    return (
      <span className="rec-name-edit">
        <input
          className="cell-input rec-name-input"
          value={draft}
          autoFocus
          disabled={busy}
          maxLength={sheet.type === 'person' ? 120 : 160}
          aria-label={sheet.type === 'person' ? 'Name' : 'Company name'}
          aria-invalid={error ? true : undefined}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              stop();
            }
          }}
        />
        {error ? (
          <span className="field-err" role="alert">
            <Icon name="warn" className="i-xs" />
            {error}
          </span>
        ) : null}
      </span>
    );
  if (readOnly) return <>{sheet.name}</>;
  return (
    <button
      type="button"
      ref={btn}
      className="rec-name-btn"
      aria-label={`${sheet.name}, rename`}
      onClick={() => {
        setDraft(sheet.name);
        done.current = false;
        setEditing(true);
      }}
    >
      {sheet.name}
    </button>
  );
}

/* ---------- tags ---------- */

function TagEditor({ sheet, readOnly }: { sheet: RecordSheet; readOnly: boolean }) {
  const patch = useRecordPatch(sheet);
  const pop = usePopover('dialog');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const all = useTags(pop.open);
  const have = new Set(sheet.tags.map((t) => t.toLowerCase()));
  const query = q.trim();
  const counts = (t: { people: number; companies: number }) => (sheet.type === 'person' ? t.people : t.companies);
  const suggestions = (all.data ?? [])
    .filter((t) => !have.has(t.tag.toLowerCase()) && (!query || t.tag.toLowerCase().includes(query.toLowerCase())))
    .sort((a, b) => counts(b) - counts(a) || a.tag.localeCompare(b.tag))
    .slice(0, 8);
  const exact = (all.data ?? []).some((t) => t.tag.toLowerCase() === query.toLowerCase());

  const write = (next: string[], before: string[]) => patch({ tags: next }, { tags: next }).then(() => before);

  const add = async (tag: string) => {
    const name = tag.trim().replace(/\s+/g, ' ');
    if (!name) return;
    if (have.has(name.toLowerCase())) {
      setError(`${sheet.name} already has “${name}”.`);
      return;
    }
    if (name.length > 40) {
      setError('Keep a tag under 40 characters');
      return;
    }
    const before = sheet.tags;
    try {
      await write([...before, name], before);
      setQ('');
      setError(null);
      pop.close();
      toast(`Tag “${name}” added`, {
        icon: 'tag',
        undo: () => {
          patch({ tags: before }, { tags: before }).catch((err) => toastError(err));
        },
      });
    } catch (err) {
      setError(fieldError(err, 'tags'));
    }
  };

  const remove = async (tag: string) => {
    const before = sheet.tags;
    try {
      await write(
        before.filter((t) => t !== tag),
        before,
      );
      toast(`Tag “${tag}” removed`, {
        icon: 'tag',
        undo: () => {
          patch({ tags: before }, { tags: before }).catch((err) => toastError(err));
        },
      });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <span className="tags">
      {sheet.tags.map((t) => (
        <span className="tag" key={t}>
          {t}
          {readOnly ? null : (
            <button type="button" className="tag-x" aria-label={`Remove tag ${t}`} onClick={() => void remove(t)}>
              <Icon name="x" />
            </button>
          )}
        </span>
      ))}
      {readOnly ? null : (
        <button type="button" className="tag tag-add" aria-label="Add tag" {...pop.anchorProps}>
          <Icon name="plus" className="i-xs" />
          Tag
        </button>
      )}
      <Popover state={pop} width={240} align="left" role="dialog" label="Add tag">
        <MenuHead>Add tag</MenuHead>
        <div className="pop-search">
          <SearchInput
            className="input-sm"
            label="Tag name"
            value={q}
            placeholder="Find or create a tag"
            autoComplete="off"
            aria-invalid={error ? true : undefined}
            maxLength={40}
            onChange={(e) => {
              setQ(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void add(suggestions.length && query && suggestions[0]!.tag.toLowerCase() === query.toLowerCase() ? suggestions[0]!.tag : query);
              }
            }}
          />
        </div>
        {error ? (
          <p className="field-err pop-note" role="alert">
            {error}
          </p>
        ) : null}
        {suggestions.map((t) => (
          <MenuItem key={t.tag} icon="tag" sub={counts(t) ? `${formatNumber(counts(t))} ${sheet.type === 'person' ? (counts(t) === 1 ? 'person' : 'people') : counts(t) === 1 ? 'company' : 'companies'}` : undefined} keep onSelect={() => void add(t.tag)}>
            {t.tag}
          </MenuItem>
        ))}
        {query && !exact && !have.has(query.toLowerCase()) ? (
          <MenuItem icon="plus" keep onSelect={() => void add(query)}>
            Create “{query}”
          </MenuItem>
        ) : null}
        {all.isPending ? <p className="pop-note muted">Loading tags…</p> : null}
        {all.isError ? <p className="pop-note muted">The team's tags did not load. Type a tag and press Enter.</p> : null}
        {!all.isPending && !suggestions.length && !query ? <p className="pop-note muted">Type a tag and press Enter.</p> : null}
      </Popover>
    </span>
  );
}

/* ---------- the logo ---------- */

/** Finding a company's logo again and removing it, for the More menu; `finding` shows on the logo tile meanwhile. */
function useLogoActions(sheet: RecordSheet) {
  const [finding, setFinding] = useState(false);
  const company = sheet.type === 'company' ? sheet : null;
  const find = async () => {
    if (!company || finding) return;
    setFinding(true);
    try {
      const res = await api.post<CompanyLogoResult>(`/companies/${company.id}/logo/find`);
      if (res.logo) toast(`Logo found on ${res.site}`, { icon: 'image' });
      else toast(`No logo on ${res.site} that works as a company logo`, { icon: 'info' });
    } catch (err) {
      toastError(err);
    } finally {
      setFinding(false);
    }
  };
  const remove = async () => {
    if (!company) return;
    try {
      const res = await api.del<CompanyLogoResult>(`/companies/${company.id}/logo`);
      const removed = res.logo;
      toast(`Logo removed from ${company.name}`, {
        icon: 'image',
        undo: removed ? () => void api.put(`/companies/${company.id}/logo`, { logo: removed }).catch((err) => toastError(err)) : undefined,
      });
    } catch (err) {
      toastError(err);
    }
  };
  return { company, finding, find, remove };
}

type LogoActions = ReturnType<typeof useLogoActions>;

/* ---------- the More menu ---------- */

function MoreMenu({ sheet, readOnly, onTab, onCompose, logo }: HeaderProps & { logo: LogoActions }) {
  const pop = usePopover('menu');
  const noun = sheet.type === 'person' ? 'person' : 'company';
  const company = logo.company;
  const copy = async () => {
    const url = `${window.location.origin}/?record=${sheet.type}/${sheet.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied', { icon: 'link' });
    } catch {
      toast(`Copy this link: ${url}`, { icon: 'link' });
    }
  };
  const remove = async () => {
    try {
      const res = await api.post<{ ids: string[] }>('/bin', { type: sheet.type, ids: [sheet.id] });
      toast(`${sheet.name} moved to the Bin`, {
        icon: 'trash',
        undo: () => {
          api
            .post('/bin/restore', { type: sheet.type, ids: res.ids })
            .then(() => openRecord(sheet.type, sheet.id))
            .catch((err) => {
              if (err instanceof ApiError && err.code === 'duplicate') actions.restoreDuplicates(sheet.type, [err.details as unknown as RestoreRefusal]);
              else toastError(err);
            });
        },
      });
      closeSheet();
    } catch (err) {
      toastError(err);
    }
  };
  return (
    <>
      <button type="button" className="icon-btn" aria-label="More actions" data-tip="More" {...pop.anchorProps}>
        <Icon name="more" />
      </button>
      <Popover state={pop} width={250} label="More actions">
        {sheet.type === 'company' && !readOnly ? (
          <>
            <MenuItem icon="mail" onSelect={() => emailRecord(sheet)}>
              Email someone here
            </MenuItem>
            <MenuItem icon="phone" onSelect={() => openLogCall(sheet)}>
              Log call
            </MenuItem>
            <MenuItem icon="note" onSelect={() => onCompose('note')}>
              Add note
            </MenuItem>
            <MenuItem icon="clock" onSelect={() => onCompose('fu')}>
              Add follow-up
            </MenuItem>
            <MenuSep />
          </>
        ) : null}
        {readOnly ? null : (
          <MenuItem icon="merge" onSelect={() => actions.merge(sheet.type, [sheet.id])}>
            Merge with another {noun}…
          </MenuItem>
        )}
        {company && !readOnly && company.logo ? (
          <MenuItem icon="image" onSelect={() => void logo.remove()}>
            Remove logo
          </MenuItem>
        ) : company && !readOnly && (company.domain || company.website) ? (
          <MenuItem icon="image" disabled={logo.finding} onSelect={() => void logo.find()}>
            {logo.finding ? 'Finding the logo…' : 'Find logo'}
          </MenuItem>
        ) : null}
        <MenuItem icon="link" onSelect={() => void copy()}>
          Copy link
        </MenuItem>
        <MenuItem icon="history" onSelect={() => onTab('history')}>
          View history
        </MenuItem>
        {readOnly ? null : (
          <>
            <MenuSep />
            <MenuItem icon="trash" danger onSelect={() => void remove()}>
              Delete {noun}
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}

/* ---------- facts ---------- */

/** "Today 07:58" for today, otherwise the usual short form. */
function lastActivity(at: string): string {
  const when = formatWhen(at);
  return /^\d/.test(when) && new Date(at).toDateString() === new Date().toDateString() ? `Today ${when}` : when;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="fl">{label}</span>
      <span className="fv">{children}</span>
    </div>
  );
}

function OwnerFact({ sheet }: { sheet: RecordSheet }) {
  return (
    <Fact label="Owner">
      {sheet.owner ? (
        <>
          <Avatar name={sheet.owner.name} hue={sheet.owner.hue} size="xs" decorative />
          {sheet.owner.name}
        </>
      ) : (
        <span className="muted">Nobody yet</span>
      )}
    </Fact>
  );
}

function PersonFacts({ sheet }: { sheet: PersonSheet }) {
  const deal = sheet.openDeals[0];
  return (
    <div className="rec-facts">
      <OwnerFact sheet={sheet} />
      {sheet.emails[0] ? <Fact label="Email">{sheet.emails[0].email}</Fact> : null}
      {sheet.phone ? <Fact label="Phone">{sheet.phone}</Fact> : null}
      <Fact label="Last contacted">{sheet.lastContactedAt ? formatAgo(sheet.lastContactedAt) : <span className="muted">Not yet</span>}</Fact>
      {deal ? (
        <Fact label={sheet.openDeals.length > 1 ? `Open deals · ${sheet.openDeals.length}` : 'Open deal'}>
          <Dot color={deal.stage?.color} />
          {formatMoneyShort(Number(deal.value), deal.currency)}
          {deal.stage ? ` · ${deal.stage.name}` : ''}
        </Fact>
      ) : null}
    </div>
  );
}

function CompanyFacts({ sheet }: { sheet: CompanySheet }) {
  const { toMain, mainCurrency } = useRates();
  const sum = sheet.openDeals.reduce((s, d) => s + toMain(Number(d.value) || 0, d.currency), 0);
  return (
    <div className="rec-facts">
      <OwnerFact sheet={sheet} />
      <Fact label="Open deals">
        {sheet.openDeals.length ? `${formatMoneyShort(sum, mainCurrency)} · ${sheet.openDeals.length}` : <span className="muted">None yet</span>}
      </Fact>
      <Fact label="People">{formatNumber(sheet.people.length)}</Fact>
      {sheet.staff !== null ? <Fact label="Staff">{formatNumber(sheet.staff)}</Fact> : null}
      {sheet.founded !== null ? <Fact label="Founded">{sheet.founded}</Fact> : null}
      {sheet.lastActivityAt ? <Fact label="Last activity">{lastActivity(sheet.lastActivityAt)}</Fact> : null}
    </div>
  );
}

/* ---------- the header ---------- */

export function Header(props: HeaderProps) {
  const { sheet, readOnly, tab, onTab, onCompose } = props;
  const person = sheet.type === 'person' ? sheet : null;
  const company = sheet.type === 'company' ? sheet : null;
  const aiOpen = Object.values(sheet.aiFilled).filter((m) => !m.reviewed).length;
  const tabsRef = useRef<HTMLDivElement>(null);
  const logo = useLogoActions(sheet);

  // A tab chosen from elsewhere (View history, a link) scrolls the tabs into view.
  const prevTab = useRef(tab);
  useEffect(() => {
    if (prevTab.current !== tab && tab === 'history') tabsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    prevTab.current = tab;
  }, [tab]);

  const city = sheet.type === 'person' ? (sheet.location ?? sheet.company?.location)?.split(',')[0] : sheet.location;

  return (
    <header className={`rec-band${company ? ' co' : ''}`}>
      <div className="rec-top">
        {company ? (
          <span className={logo.finding ? 'rec-logo finding' : 'rec-logo'}>
            <CompanyMono name={sheet.name} hue={sheet.hue} logo={company.logo} size="xl" />
            {logo.finding ? <Spinner label={`Finding the logo of ${company.name}`} /> : null}
          </span>
        ) : (
          <Avatar name={sheet.name} hue={sheet.hue} size="xl" decorative />
        )}
        <div className="rec-id">
          <h1 className="rec-name">
            <Name sheet={sheet} readOnly={readOnly} />
            {person?.doNotEmail ? (
              <Chip tone="danger" icon="lock">
                Do not email
              </Chip>
            ) : null}
            {company?.standing === 'customer' ? <Chip tone="success">Customer</Chip> : null}
            {company?.standing === 'lost' ? <Chip>Lost</Chip> : null}
            {sheet.duplicates[0] && !readOnly ? (
              <button
                type="button"
                className="chip chip-warning"
                onClick={() => actions.merge(sheet.type, [sheet.id, ...sheet.duplicates.map((d) => d.id)])}
                data-tip={`Looks like ${sheet.duplicates.map((d) => d.name).join(', ')}. Review and merge`}
              >
                <Icon name="merge" />
                Possible duplicate
              </button>
            ) : null}
          </h1>
          <div className="rec-role">
            {person ? (
              <>
                {person.title ? <span>{person.title}</span> : null}
                {person.title && person.company ? <span className="sep">·</span> : null}
                {person.company ? (
                  <>
                    <CompanyMono name={person.company.name} hue={person.company.hue} logo={person.company.logo} size="xs" />
                    <button type="button" className="link" onClick={() => openRecord('company', person.company!.id)}>
                      {person.company.name}
                    </button>
                  </>
                ) : null}
              </>
            ) : company ? (
              <>
                {company.market ? <span className="rec-market">{company.market}</span> : null}
                {company.market && company.industry && !sameTerm(company.market, company.industry) ? (
                  <>
                    <span className="sep">·</span>
                    <span>{company.industry}</span>
                  </>
                ) : !company.market && company.industry ? (
                  <span>{company.industry}</span>
                ) : null}
                {(company.market || company.industry) && (company.domain || company.website) ? <span className="sep">·</span> : null}
                {company.domain || company.website ? (
                  <a className="link" href={hrefOf(company.website ?? company.domain!)} target="_blank" rel="noreferrer">
                    {company.domain ?? bareUrl(company.website!)}
                  </a>
                ) : null}
              </>
            ) : null}
            {city ? (
              <>
                <span className="sep">·</span>
                <Icon name="pin" className="i-sm" />
                {city}
              </>
            ) : null}
          </div>
          <div className="rec-meta">
            <TagEditor sheet={sheet} readOnly={readOnly} />
            {sheet.source ? (
              <span className="chip rec-src" title={sheet.sourceDetail ?? undefined}>
                <Icon name="flag" />
                {sheet.sourceDetail ? `${sheet.source} · ${sheet.sourceDetail}` : sheet.source}
              </span>
            ) : null}
            {aiOpen ? (
              <span className="chip chip-info">
                <Icon name="sparkle" />
                {aiOpen === 1 ? '1 field filled by AI' : `${aiOpen} fields filled by AI`}
              </span>
            ) : null}
          </div>
        </div>
        {readOnly ? (
          <div className="rec-acts">
            <MoreMenu {...props} logo={logo} />
          </div>
        ) : person ? (
          <div className="rec-acts">
            <button type="button" className="btn btn-primary" onClick={() => emailRecord(sheet)} data-tip={`Email ${firstName(sheet.name)}`} data-key="E">
              <Icon name="mail" />
              Email
            </button>
            <button type="button" className="btn btn-tile" onClick={() => openLogCall(sheet)}>
              <Icon name="phone" />
              Log call
            </button>
            <button type="button" className="btn btn-tile" onClick={() => onCompose('note')}>
              <Icon name="note" />
              Note
            </button>
            <button type="button" className="btn btn-tile" onClick={() => onCompose('fu')}>
              <Icon name="clock" />
              Follow-up
            </button>
            <button type="button" className="btn btn-tile" onClick={() => onCompose('comment')}>
              <Icon name="comment" />
              Comment
            </button>
            <MoreMenu {...props} logo={logo} />
          </div>
        ) : (
          <div className="rec-acts">
            <button type="button" className="btn btn-primary" onClick={() => actions.newDeal({ companyId: sheet.id })}>
              <Icon name="plus" />
              New deal
            </button>
            <button type="button" className="btn btn-tile" onClick={() => actions.newRecord('person', { companyId: sheet.id })}>
              <Icon name="user-plus" />
              Add person
            </button>
            <button type="button" className="btn btn-tile" onClick={() => onCompose('comment')}>
              <Icon name="comment" />
              Comment
            </button>
            <MoreMenu {...props} logo={logo} />
          </div>
        )}
      </div>
      {person ? <PersonFacts sheet={person} /> : company ? <CompanyFacts sheet={company} /> : null}
      <div ref={tabsRef}>
        <Tabs
          className="rec-tabs"
          label="Record sections"
          value={tab}
          onChange={onTab}
          tabs={[
            { id: 'timeline', label: 'Timeline', icon: 'activity', count: sheet.counts.timeline },
            { id: 'comments', label: 'Comments', icon: 'comment', count: sheet.counts.comments },
            { id: 'history', label: 'History', icon: 'history', count: sheet.counts.history },
            { id: 'files', label: 'Files', icon: 'file', count: sheet.counts.files },
          ]}
        />
      </div>
    </header>
  );
}
